import React, { useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { clearAuth, getAuth, isSuperadmin, setAuth } from "./adminApi";
import Themes from "./pages/Themes";
import Rules from "./pages/Rules";
import Content from "./pages/Content";
import SuperadminLogin from "./pages/SuperadminLogin";
import "./admin.css";

const GAME_TITLE = "Dreamcatcher";
const SUPERADMIN_TAB_KEY = "adminSuperLogin";

/**
 * The PHP admin was entered through admin_trigger.php?sessionId=..&userId=..&organizationId=..;
 * here /admin?sessionId=..&userId=..&organizationId=.. stores the same values for the API calls.
 * The game also leaves them in sessionStorage('userData') after login.
 */
function lastGameSession() {
  try {
    const last = JSON.parse(localStorage.getItem("adminGameSession") || "null");
    return last?.sessionId && last?.organizationId ? last : null;
  } catch {
    return null;
  }
}

function bootstrapSession(search, pathname) {
  const params = new URLSearchParams(search);
  const sessionId = params.get("sessionId");
  const organizationId = params.get("organizationId");
  if (sessionId && organizationId) {
    const next = { sessionId, organizationId, userId: params.get("userId") };
    setAuth(next);
    sessionStorage.removeItem(SUPERADMIN_TAB_KEY);
    return next;
  }
  const stored = getAuth();
  // a superadmin login made in this tab wins
  if (stored && sessionStorage.getItem(SUPERADMIN_TAB_KEY) === "1") return stored;

  // opening /admin itself is a fresh entry: use the newest game login, not a session an earlier visit left in this tab
  const isEntry = pathname.replace(/\/+$/, "") === "/admin";
  const last = lastGameSession();
  if (isEntry && last) {
    setAuth(last);
    return last;
  }
  // a reload deeper in the admin (/admin/themes ...) keeps this tab's session
  if (stored?.sessionId === "admin") clearAuth();
  else if (stored) return stored;

  if (last) {
    setAuth(last);
    return last;
  }
  return null;
}

function Header() {
  const redirectGameSession = () => {
    try {
      const gameId = JSON.parse(sessionStorage.getItem("userData") || "null")?.gameId;
      if (gameId == null) return;
      window.location.href = process.env.REACT_APP_BASE_URL + "/active-games/" + gameId;
    } catch {
      /* no game session */
    }
  };

  return (
    <div className="header-admin">
      <div className="brand-logo-and-game-holder">
        <div className="brand-logo-holder" onClick={() => { window.location.href = process.env.REACT_APP_BASE_URL || "/"; }}>
          <img src="/admin/play.png" className="brand-logo" alt="Extramileplay" />
          {isSuperadmin() && " SUPERADMIN"}
        </div>
        <div onClick={redirectGameSession}>{GAME_TITLE}</div>
      </div>
      <div className="user">
        <img src="/admin/user.png" alt="" />
      </div>
    </div>
  );
}

function Protected({ auth, children }) {
  if (!auth) return <Navigate to="/admin/superadmin" replace />;
  return children;
}

export default function Admin() {
  const location = useLocation();
  useState(() => bootstrapSession(location.search, location.pathname)); // store the entry session once, before the first render
  const auth = getAuth(); // read on every render so login / logout take effect immediately

  return (
    <div className="dc-admin admin-background">
      <Routes>
        <Route path="/admin/superadmin" element={<SuperadminLogin />} />
        <Route
          path="*"
          element={
            <>
              <Header />
              <Routes>
                <Route path="/admin/themes" element={<Protected auth={auth}><Themes /></Protected>} />
                <Route path="/admin/rules/:themeName" element={<Protected auth={auth}><Rules /></Protected>} />
                <Route path="/admin/content/:themeName" element={<Protected auth={auth}><Content /></Protected>} />
                {/* /admin and anything unknown: land on the themes list once the session is known */}
                <Route
                  path="*"
                  element={auth ? <Navigate to="/admin/themes" replace /> : <Protected auth={auth} />}
                />
              </Routes>
            </>
          }
        />
      </Routes>
    </div>
  );
}
